// Library methods
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import DefaultModal from "src/components/shared/modals/DefaultModal/DefaultModal";
import TextInputField from "src/components/shared/inputs/TextInputField/TextInputField";
import CustomAutocomplete from "src/components/shared/inputs/CustomAutocomplete/CustomAutocomplete";
import { CloseIcon } from "src/assets/icons";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";

// Hooks
import { useUpdateEMRHub } from "src/services/emr/hooks/emr-hubs/useUpdateEMRHub";
import { useUpdateEMRHubClinics } from "src/services/emr/hooks/emr-hubs/useUpdateEMRHubClinics";

// Styles
import {
  getRequiredErrorColor,
  ModalCustomCancel,
  ModalCustomConfirm,
} from "src/styles/mui/muiStylesHelper";
import { useTheme } from "@mui/material/styles";

type AutocompleteOptionType = { displayName: string; value: string };

const EMPTY_CLINIC_OPTION: AutocompleteOptionType = {
  displayName: "",
  value: "",
};

type EditEMRHubModalType = {
  open: boolean;
  clinicId: string | undefined;
  hubId: string | undefined;
  currentAlias: string;
  homeClinicId: string;
  currentAssociatedClinics: Array<{ clinicId: string; name: string }>;
  onClose: () => void;
};

const EditEMRHubModal = ({
  open,
  clinicId,
  hubId,
  currentAlias,
  homeClinicId,
  currentAssociatedClinics,
  onClose,
}: EditEMRHubModalType) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [alias, setAlias] = useState(currentAlias);
  const [associatedClinics, setAssociatedClinics] = useState(
    currentAssociatedClinics
  );
  const [clinicSearch, setClinicSearch] =
    useState<AutocompleteOptionType>(EMPTY_CLINIC_OPTION);

  // filtered list of clinics the user has access to
  const { allClinicInfo } = useClinicContext();
  const clinicOptions = useMemo(() => {
    const selectedIds = new Set(
      associatedClinics.map((clinic) => clinic.clinicId)
    );
    return (allClinicInfo ?? [])
      .filter((clinic) => !selectedIds.has(clinic.id))
      .map((clinic) => ({ displayName: clinic.name, value: clinic.id }));
  }, [allClinicInfo, associatedClinics]);

  const orderedClinics = useMemo(
    () => [
      ...associatedClinics.filter((clinic) => clinic.clinicId === homeClinicId),
      ...associatedClinics.filter((clinic) => clinic.clinicId !== homeClinicId),
    ],
    [associatedClinics, homeClinicId]
  );

  const { mutateAsync: updateHub, isPending: isUpdatingHub } =
    useUpdateEMRHub(clinicId);
  const { mutateAsync: updateHubClinics, isPending: isUpdatingClinics } =
    useUpdateEMRHubClinics(clinicId);
  const isPending = isUpdatingHub || isUpdatingClinics;

  // Order doesn't matter, only which clinics are on the hub -- an untouched
  // list skips the clinics request entirely.
  const haveClinicsChanged = useMemo(() => {
    const currentIds = new Set(
      currentAssociatedClinics.map((clinic) => clinic.clinicId)
    );
    return (
      associatedClinics.length !== currentIds.size ||
      associatedClinics.some((clinic) => !currentIds.has(clinic.clinicId))
    );
  }, [associatedClinics, currentAssociatedClinics]);

  // initialize values each time the modal is re-opened
  useEffect(() => {
    if (!open) return;

    setAlias(currentAlias);
    setAssociatedClinics(currentAssociatedClinics);
    setClinicSearch(EMPTY_CLINIC_OPTION);
  }, [open, currentAlias, currentAssociatedClinics]);

  const handleClose = useCallback(() => {
    setAlias(currentAlias);
    setAssociatedClinics(currentAssociatedClinics);
    setClinicSearch(EMPTY_CLINIC_OPTION);
    onClose();
  }, [currentAlias, currentAssociatedClinics, onClose]);

  const handleClinicSearchChange = useCallback(
    (option: AutocompleteOptionType) => {
      const picked = clinicOptions.find(
        (candidate) => candidate.value === option.value
      );

      if (!picked) return;

      setAssociatedClinics((prev) => [
        ...prev,
        { clinicId: picked.value, name: picked.displayName },
      ]);
      setClinicSearch({ ...EMPTY_CLINIC_OPTION });
    },
    [clinicOptions]
  );

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      setAlias(event.target.value);
    },
    []
  );

  // update the associated clinics and then the hub alias
  const handleConfirm = useCallback(async () => {
    if (!hubId) return;

    try {
      if (haveClinicsChanged) {
        await updateHubClinics({
          hubId,
          clinicIds: orderedClinics.map((clinic) => clinic.clinicId),
        });
      }
      await updateHub({ hubId, alias });
      onClose();
    } catch {
      // do nothing
    }
  }, [
    hubId,
    haveClinicsChanged,
    updateHubClinics,
    orderedClinics,
    updateHub,
    alias,
    onClose,
  ]);

  return (
    <DefaultModal
      isOpen={open}
      onClose={handleClose}
      title={t("Edit EMR Hub")}
      maxWidth="sm"
      modalActions={
        <Grid sx={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
          <Button sx={ModalCustomCancel} onClick={handleClose}>
            {t("Cancel")}
          </Button>
          <Button
            sx={ModalCustomConfirm}
            onClick={handleConfirm}
            disabled={
              !alias.trim() || associatedClinics?.length === 0 || isPending
            }
          >
            {t("Save")}
          </Button>
        </Grid>
      }
    >
      <Grid display="flex" flexDirection="column" p="16px" gap="8px">
        <Box display="flex" flexDirection="column" gap="4px">
          <Typography fontSize="12px">
            {t("Alias")}
            <span style={{ color: getRequiredErrorColor(theme) }}>*</span>
          </Typography>
          <TextInputField
            id="edit-emr-hub-alias-input"
            autoFocus
            required
            placeholder={t("Enter a value")}
            value={alias}
            onChange={handleChange}
            maxLength={50}
          />
        </Box>

        <Box display="flex" flexDirection="column" gap="4px">
          <Typography fontSize="12px">
            {t("Associated Clinics")}
            <span style={{ color: getRequiredErrorColor(theme) }}>*</span>
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "4px",
                padding: "8px",
                borderRadius: "4px",
                backgroundColor:
                  theme.palette.mode === "dark" ? "#20233840" : "#5B6D7A20",
              }}
            >
              {orderedClinics.length > 0 ? (
                orderedClinics.map((clinic, index) => (
                  <Box
                    key={clinic.clinicId}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: "8px",
                    }}
                  >
                    {clinic.clinicId === homeClinicId ? (
                      <Typography
                        sx={{
                          maxWidth: "fit-content",
                          display: "block",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          fontSize: "14px",
                          fontWeight: 600,
                        }}
                      >
                        {t("Home Clinic")}: {clinic.name}
                      </Typography>
                    ) : (
                      <Typography
                        sx={{
                          maxWidth: "fit-content",
                          display: "block",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          fontSize: "14px",
                        }}
                      >
                        {index}. {clinic.name}
                      </Typography>
                    )}

                    {clinic.clinicId !== homeClinicId && (
                      <IconButton
                        disableFocusRipple
                        disableTouchRipple
                        disableRipple
                        aria-label={t("Remove Clinic")}
                        color="inherit"
                        onClick={() =>
                          setAssociatedClinics((prev) => {
                            const test = prev.filter(
                              (item) => item.clinicId !== clinic.clinicId
                            );
                            return test;
                          })
                        }
                        sx={{ p: 0 }}
                      >
                        <CloseIcon height="16px" width="16px" />
                      </IconButton>
                    )}
                  </Box>
                ))
              ) : (
                <Typography fontSize="14px">
                  {t("No clinics selected.")}
                </Typography>
              )}
            </Box>

            <CustomAutocomplete
              id="edit-emr-hub-associated-clinics-autocomplete"
              data={clinicOptions}
              value={clinicSearch}
              onChange={handleClinicSearchChange}
            />
          </Box>
        </Box>
      </Grid>
    </DefaultModal>
  );
};

export default EditEMRHubModal;
